import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import {
  Header,
  Footer,
  Container,
  TwoColumnGrid,
  LoadingSpinner,
  ErrorMessage,
  InfoCard,
} from './components/Layout';
import DateSelector from './components/DateSelector';
import PriceChart from './components/PriceChart';
import PriceComparisonChart from './components/PriceComparisonChart';
import VolumeChart from './components/VolumeChart';
import PasswordLogin from './components/PasswordLogin';
import PriceToggle from './components/PriceToggle';
import api from './services/api';
import { CombinedPriceData } from './types/price';
import './App.css';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [priceData, setPriceData] = useState<CombinedPriceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isBrutto, setIsBrutto] = useState<boolean>(true); // Default is Brutto

  // Check authentication on mount
  useEffect(() => {
    const authStatus = localStorage.getItem('energy-forecast-auth');
    setIsAuthenticated(authStatus === 'true');
  }, []);

  // Load available dates on mount
  useEffect(() => {
    if (isAuthenticated) {
      loadAvailableDates();
    }
  }, [isAuthenticated]);

  // Load price data when date changes
  useEffect(() => {
    if (selectedDate && isAuthenticated) {
      loadPriceData(selectedDate);
    }
  }, [selectedDate, isAuthenticated]);

  const loadAvailableDates = async () => {
    try {
      const response = await api.getAvailableDates();
      const dates = response.dates.map(d => d.date);
      setAvailableDates(dates);

      // If today's data is not available, select the most recent date
      const today = format(new Date(), 'yyyy-MM-dd');
      if (!dates.includes(today) && dates.length > 0) {
        setSelectedDate(dates[0]);
      }
    } catch (err) {
      console.error('Error loading available dates:', err);
      setError('Fehler beim Laden der verfügbaren Daten');
    }
  };

  const loadPriceData = async (date: string) => {
    setLoading(true);
    setError(null);

    // Check if this is tomorrow's date
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = format(tomorrow, 'yyyy-MM-dd');
    const isTomorrow = date === tomorrowStr;

    try {
      const data = await api.getCombinedPrices(date);
      setPriceData(data);
    } catch (err) {
      console.error('Error loading price data:', err);

      // Don't show error for tomorrow - we expect missing data
      if (!isTomorrow) {
        setError('Fehler beim Laden der Preisdaten');
      }
      setPriceData(null);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentPriceInfo = () => {
    if (!priceData) return null;

    const now = new Date();
    const currentHour = now.getHours();

    // Find current hour price from day-ahead data
    const currentHourPrice = priceData.dayAhead?.prices.find(p => {
      const priceDate = new Date(p.timestamp);
      return priceDate.getHours() === currentHour;
    })?.price;

    // Find current 15-min price from intraday data
    const currentQuarter = Math.floor(now.getMinutes() / 15);
    const currentQuarterPrice = priceData.intraday?.prices.find(p => {
      const priceDate = new Date(p.timestamp);
      return priceDate.getHours() === currentHour && 
             Math.floor(priceDate.getMinutes() / 15) === currentQuarter;
    })?.price;

    return {
      hourly: currentHourPrice,
      quarterly: currentQuarterPrice,
    };
  };

  const formatLastUpdate = (data: CombinedPriceData | null) => {
    if (!data) return '';

    const dayAheadUpdate = data.dayAhead?.lastUpdate;
    const intradayUpdate = data.intraday?.lastUpdate;

    const mostRecent = [dayAheadUpdate, intradayUpdate]
      .filter(Boolean)
      .sort()
      .reverse()[0];

    if (mostRecent) {
      return format(new Date(mostRecent), 'dd.MM.yyyy HH:mm');
    }

    return '';
  };

  const handleLogin = (password: string) => {
    setIsAuthenticated(true);
    localStorage.setItem('energy-forecast-auth', 'true');
  };

  // Price conversion function
  const convertPrice = (nettoPrice: number): number => {
    if (isBrutto) {
      // Brutto ≈ (Spotpreis + Tibber Beschaffung + Netzentgelt + Umlagen & Abgaben) * 1,19 (MwSt.)
      // Convert from EUR/MWh to ct/kWh first (divide by 10), then add fixed costs and MwSt.
      const nettoCentPerKwh = nettoPrice / 10;
      const nettoTotal = nettoCentPerKwh + 0.72 + 10.9 + 2.65;
      return (nettoTotal * 1.19) * 10; // Add 19% MwSt. and convert back to match original scale
    }
    return nettoPrice; // Return netto price as-is
  };


  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <PasswordLogin onLogin={handleLogin} />;
  }

  const currentPrices = getCurrentPriceInfo();
  const lastUpdate = formatLastUpdate(priceData);

  return (
    <div className="app">
      <Header />

      <main className="main-content">
        <Container>
          <div className="controls-section">
            <DateSelector
              selectedDate={selectedDate}
              availableDates={availableDates}
              onDateChange={setSelectedDate}
            />
            {priceData && (priceData.dayAhead || priceData.intraday) && (
            <PriceToggle
              isBrutto={isBrutto}
              onToggle={setIsBrutto}
            />
            )}
            {lastUpdate && (
              <div className="last-update">
                Letztes Update: {lastUpdate}
              </div>
            )}
          </div>

          {loading && <LoadingSpinner />}

          {error && <ErrorMessage message={error} />}

          {!loading && !error && (
            <>
              {/* Show waiting message for tomorrow only if no day-ahead data */}
              {selectedDate === format(new Date(Date.now() + 24 * 60 * 60 * 1000), 'yyyy-MM-dd') && !priceData?.dayAhead && (
                <div className="waiting-message" style={{
                  background: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
                  border: '1px solid #ddd',
                  borderRadius: '12px',
                  padding: '2rem',
                  margin: '2rem 0',
                  textAlign: 'center'
                }}>
                  <h2 style={{ color: '#666', marginBottom: '1rem' }}>
                    ⏳ Warte auf Preise von morgen
                  </h2>
                  <p style={{ color: '#888', fontSize: '1.1rem', lineHeight: '1.5' }}>
                    Die Strompreise für morgen stehen heute gegen <strong>13:00</strong> und <strong>15:00</strong> Uhr zur Verfügung.
                  </p>
                </div>
              )}

              {priceData && (
                <>
                  {/* Info cards for tomorrow - show day-ahead stats */}
                  {selectedDate === format(new Date(Date.now() + 24 * 60 * 60 * 1000), 'yyyy-MM-dd') && priceData.dayAhead && !priceData.intraday && (
                    <div className="current-prices-grid">
                      {priceData.dayAhead?.baseload !== undefined && (
                        <InfoCard
                          title="Tagesdurchschnitt für morgen"
                          value={(convertPrice(priceData.dayAhead.baseload) / 10).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          unit="ct/kWh"
                          highlight
                          infoText="Der Tagesdurchschnitt (Baseload) ist der Durchschnittspreis für alle 24 Stunden des Tages. Dieser Wert repräsentiert die Grundlast im Stromnetz."
                          infoTitle="Was ist der Tagesdurchschnitt?"
                        />
                      )}
                      {priceData.dayAhead?.peakload !== undefined && (
                        <InfoCard
                          title="Peakload für morgen"
                          value={(convertPrice(priceData.dayAhead.peakload) / 10).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          unit="ct/kWh"
                          infoText="Peakload ist der Durchschnittspreis während der Hauptverbrauchszeiten (Mo-Fr 8-20 Uhr). In diesen Zeiten ist der Stromverbrauch am höchsten."
                          infoTitle="Was ist Peakload?"
                        />
                      )}
                    </div>
                  )}
                  
                  {/* Current prices info cards - only show for today */}
                  {selectedDate === format(new Date(), 'yyyy-MM-dd') && currentPrices && (
                <div className="current-prices-grid">
                  {currentPrices.hourly !== undefined && (
                    <InfoCard
                      title="Aktueller Stundenpreis"
                      value={(convertPrice(currentPrices.hourly) / 10).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      unit="ct/kWh"
                      highlight
                    />
                  )}
                  {currentPrices.quarterly !== undefined && (
                    <InfoCard
                      title="Aktueller 15-Min Preis"
                      value={(convertPrice(currentPrices.quarterly) / 10).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      unit="ct/kWh"
                    />
                  )}
                  {priceData.dayAhead?.baseload !== undefined && (
                    <InfoCard
                      title="Tagesdurchschnitt"
                      value={(convertPrice(priceData.dayAhead.baseload) / 10).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      unit="ct/kWh"
                      infoText="Der Tagesdurchschnitt (Baseload) ist der Durchschnittspreis für alle 24 Stunden des Tages. Dieser Wert repräsentiert die Grundlast im Stromnetz."
                      infoTitle="Was ist der Tagesdurchschnitt?"
                    />
                  )}
                  {priceData.dayAhead?.peakload !== undefined && (
                    <InfoCard
                      title="Peakload"
                      value={(convertPrice(priceData.dayAhead.peakload) / 10).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      unit="ct/kWh"
                      infoText="Peakload ist der Durchschnittspreis während der Hauptverbrauchszeiten (Mo-Fr 8-20 Uhr). In diesen Zeiten ist der Stromverbrauch am höchsten."
                      infoTitle="Was ist Peakload?"
                    />
                  )}
                </div>
              )}

              {/* For tomorrow: Show day-ahead chart if available, otherwise main chart */}
              {selectedDate === format(new Date(Date.now() + 24 * 60 * 60 * 1000), 'yyyy-MM-dd') && priceData?.dayAhead && !priceData?.intraday && (
                <PriceChart
                  dayAheadData={priceData.dayAhead}
                  intradayData={null}
                  title={`Strompreise für morgen mit Tagesdurchschnitt (${isBrutto ? 'Brutto' : 'Netto'})`}
                  isBrutto={isBrutto}
                  convertPrice={convertPrice}
                  showCurrentTime={false}
                />
              )}
              
              {/* Main price chart with gradient colors - for today or when both data sources available */}
              {priceData?.dayAhead && priceData?.intraday && (
                <PriceChart
                  dayAheadData={priceData.dayAhead}
                  intradayData={priceData.intraday}
                  title={`Strompreise mit Tagesdurchschnitt (${isBrutto ? 'Brutto' : 'Netto'})`}
                  isBrutto={isBrutto}
                  convertPrice={convertPrice}
                  showCurrentTime={selectedDate === format(new Date(), 'yyyy-MM-dd')}
                />
              )}
              
              {/* Comparison chart showing both day-ahead and intraday */}
              {priceData?.dayAhead && priceData?.intraday && (
                <PriceComparisonChart
                  dayAheadData={priceData.dayAhead}
                  intradayData={priceData.intraday}
                  title={`Vergleich: Day-Ahead vs. Intraday (${isBrutto ? 'Brutto' : 'Netto'})`}
                  isBrutto={isBrutto}
                  convertPrice={convertPrice}
                  showCurrentTime={selectedDate === format(new Date(), 'yyyy-MM-dd')}
                />
              )}

              {/* Volume charts */}
              {priceData.dayAhead && priceData.intraday ? (
                <TwoColumnGrid>
                  <VolumeChart
                    data={priceData.dayAhead}
                    title="Day-Ahead Volumen"
                  />
                  <VolumeChart
                    data={priceData.intraday}
                    title="Intraday Volumen"
                  />
                </TwoColumnGrid>
              ) : (
                <>
                  {priceData.dayAhead && (
                    <VolumeChart
                      data={priceData.dayAhead}
                      title="Day-Ahead Volumen"
                    />
                  )}
                  {priceData.intraday && (
                    <VolumeChart
                      data={priceData.intraday}
                      title="Intraday Volumen"
                    />
                  )}
                </>
              )}
                </>
              )}
            </>
          )}
        </Container>
      </main>

      <Footer />
    </div>
  );
}

export default App;